import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { EntityHierarchyControllerv2Service, RequestTypeValueEnum } from '@app/generated-api';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { catchError, map, of } from 'rxjs';

/**
 * Ensures the chained route ids (study/site/patient/request) that appear together in a
 * URL actually belong to one another, blocking navigation when the URL is self-contradictory
 * (e.g. a site that belongs to a different study than the one in the URL).
 */
export const entityHierarchyGuard: CanActivateFn = (route) => {
    const entityHierarchyService = inject(EntityHierarchyControllerv2Service);
    const router = inject(Router);
    const snackbarService = inject(SnackbarService);

    const studyId = toId(route.paramMap.get('studyId') ?? route.paramMap.get('id'));
    const siteId = toId(route.paramMap.get('siteId'));
    const patientId = toId(route.paramMap.get('patientId'));
    const requestId = toId(route.paramMap.get('requestId'));
    const requestType = resolveRequestType(
        route.queryParamMap.get('payment-travel'),
        route.queryParamMap.get('stipend-oop')
    );

    const relevantIdCount = [studyId, siteId, patientId, requestId].filter((id) => id !== null).length;
    if (relevantIdCount < 2) return true;

    return entityHierarchyService
        .entityHierarchyControllerv2Validate(studyId, siteId, patientId, requestId, requestType)
        .pipe(
            map(() => true),
            catchError((error: unknown) => {
                // Only a genuine 404 means the URL is self-contradictory. Any other failure
                // (e.g. an auth/token timing error on a cold page load, where the request never
                // completes) must not masquerade as a missing page and block navigation.
                if ((error as { status?: number } | null)?.status !== 404) {
                    return of(true);
                }

                snackbarService.openErrorSnackbar('The requested page could not be found.');
                return of(router.parseUrl('/'));
            })
        );
};

function toId(value: string | null): number | null {
    if (!value) return null;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function resolveRequestType(paymentTravel: string | null, stipendOop: string | null): RequestTypeValueEnum | null {
    if (paymentTravel === 'travel') return RequestTypeValueEnum.TRAVEL;
    if (paymentTravel === 'payment' && stipendOop === 'stipend') return RequestTypeValueEnum.STIPEND;
    if (paymentTravel === 'payment' && stipendOop === 'out-of-pocket') return RequestTypeValueEnum.REIMBURSEMENT;

    return null;
}
